import React, { useState } from 'react';
import { Typography, Box, Avatar, Chip, Button } from '@mui/material';
import { Circle as CircleIcon, Chat as ChatIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useTheme } from '@mui/material/styles';

const initials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  return parts.length ? (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() : '?';
};

/** A new message in a manuscript's team chat. Opens the manuscript with the chat panel open. */
export default function ProposalChatNotificationCard({ notification, onMarkRead }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const p = notification.payload || {};
  const [broken, setBroken] = useState(false);
  const isPublication = p.document_type === 'publication';
  // author_avatar_url is absolute (built by the API); author_avatar is a bare /media path
  const avatar = !broken && (p.author_avatar_url || null);
  const name = p.proposal_acronym || p.proposal_title || 'a manuscript';

  const open = (e) => {
    e?.stopPropagation();
    if (!notification.is_read && onMarkRead) onMarkRead();
    if (p.consortium_id && p.proposal_id) {
      navigate(`/consortium/${p.consortium_id}/${isPublication ? 'publications' : 'proposals'}/${p.proposal_id}/?panel=chat`);
    } else if (p.consortium_id) {
      navigate(`/consortium/${p.consortium_id}/`);
    }
  };

  return (
    <Box
      onClick={open}
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.25, px: 2,
        borderBottom: '1px solid', borderColor: 'divider',
        backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
        cursor: 'pointer', '&:hover': { backgroundColor: 'action.selected' },
      }}
    >
      <Avatar src={avatar || undefined} onError={() => setBroken(true)}
        sx={{ width: 34, height: 34, mt: 0.25, fontSize: 13, bgcolor: theme.palette.primary.avatar, color: 'white' }}>
        {p.author_name ? initials(p.author_name) : <ChatIcon sx={{ fontSize: 18 }} />}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant="body2">
            <Box component="span" fontWeight={600}>{p.author_name || 'Someone'}</Box>
            <Box component="span" color="text.secondary"> wrote in the team chat of </Box>
            <Box component="span" fontWeight={600}>{name}</Box>
          </Typography>
          <Chip size="small" icon={<ChatIcon sx={{ fontSize: '0.85rem !important' }} />} label="Team chat"
            sx={{ height: 18, fontSize: '0.7rem', fontWeight: 600, bgcolor: 'rgba(124,58,237,0.1)', color: '#7c3aed' }} />
        </Box>
        {p.body_preview && (
          <Typography
            variant="body2"
            sx={{
              mt: 0.5, pl: 1, borderLeft: 3, borderColor: 'primary.light', color: 'text.secondary',
              overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              fontStyle: 'italic',
            }}
          >
            {p.body_preview}
          </Typography>
        )}
        <Button size="small" onClick={open} sx={{ mt: 0.5, px: 0.75, minWidth: 0, textTransform: 'none' }}>
          Open chat
        </Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatDistanceToNow(new Date(notification.created), { addSuffix: true })}
        </Typography>
        {!notification.is_read && <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />}
      </Box>
    </Box>
  );
}
